import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { replace } from 'connected-react-router';

import Chevron from 'public/chevron.svg';
import Cross from 'public/cross.svg';
import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import type { NextBestActionCardProps } from 'src/app/types';

import { NEXT_BEST_ACTIONS } from './constants';
import {
  ChevronIcon,
  CloseButtonWrap,
  CloseCardButton,
  Description,
  IconContainer,
  LinkContainer,
  Row,
  StyledCard,
  StyledIcon,
  StyledLink,
} from './styles';

const segmentProps = {
  ad_location: 'top',
  ad_page: 'Your Prescriptions',
  ad_position: 1,
};

const NextBestActionCard = ({
  handleDismissNBACard,
  isDismissing,
  visibleNextBestActionCard,
}: NextBestActionCardProps) => {
  const dispatch = useDispatch();
  const closeIconRef = useRef(null);

  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload) as any),
    [dispatch]
  );

  const { description, action, link, segmentName, iconSource, color, Component } =
    NEXT_BEST_ACTIONS[visibleNextBestActionCard] ?? {};

  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    dispatchSegmentTrack(UNIFIED.viewAdSpace, { ...segmentProps, ad_action: segmentName });
  }, [dispatchSegmentTrack, segmentName]);

  const handleCardClick = () => {
    dispatchSegmentTrack(UNIFIED.selectAdSpace, { ...segmentProps, ad_action: segmentName });
    if (link) dispatch(replace(link));
  };

  const handleCloseCardClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    dispatchSegmentTrack(UNIFIED.dismissAdSpace, { ...segmentProps, ad_action: segmentName });
    handleDismissNBACard(visibleNextBestActionCard);
  };

  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (target !== closeIconRef.current) {
      setIsHovered(true);
    }
  };
  if (Component) {
    return (
      <Component
        handleCardClick={handleCardClick}
        isDismissing={isDismissing}
        handleCloseCardClick={handleCloseCardClick}
      />
    );
  }
  return (
    <StyledCard
      className="styled-card"
      color={color}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      isDismissing={isDismissing}
    >
      <Row>
        <Description>{description}</Description>
        <CloseButtonWrap ref={closeIconRef} onClick={handleCloseCardClick}>
          <CloseCardButton src={Cross} />
        </CloseButtonWrap>
      </Row>
      <Row>
        <LinkContainer>
          <StyledLink to={link} showUnderline={isHovered}>
            {action}
          </StyledLink>
          <ChevronIcon src={Chevron} color={color} />
        </LinkContainer>
        <IconContainer color={color}>
          <StyledIcon src={iconSource} />
        </IconContainer>
      </Row>
    </StyledCard>
  );
};

export default NextBestActionCard;
